import { EMAIL_SUBSCRIBE, EMAIL_UNSUBSCRIBE } from 'constants/esDataTypes';
import { getQueryStub } from 'modules/workflowNodeEditors/components/startNode/utils';
import { BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { BADGE_VARIANTS } from 'ui/badge';
import { Delete } from 'ui/icons/action/Delete';
import { colors } from 'ui/theme';
import { IS_NOT_SET, IS_SET } from 'utils/comparatorTypes';
import { useSelector } from 'react-redux';
import {
  selectIsJourneyGoalsFeatureFlagOn,
  selectIsJourneyGoalsWithExitsFeatureFlagOn,
} from 'core/userContext/selectors';
import {
  getJourneyCurrentConversion,
  getJourneyCurrentConversionEvent,
} from 'modules/workflows/ducks/selectors/workflow';
import { getIsJourneyExitEventsEnabled } from 'modules/workflows/ducks';
import { Tooltip } from 'ui/tooltip/Tooltip';
import { Popover, PopoverContent, PopoverTrigger } from 'ui/popover';
import { FilterSegmentSelectInputOption, Query } from '../../FilterInputsList';
import { ITERABLE_PURCHASE_EVENT_NAME, RULE_TYPES } from '../constants';
import { useRuleBuilder } from '../hooks/useRuleBuilder';
import { Rule, SendToJourneyMetadata } from '../types';
import { ChildRule } from './ChildRule';
import { isFirstRuleFilledOut } from './RuleBlock.utils';
import {
  StyledActionArea,
  StyledBadge,
  StyledBadgeContainer,
  StyledBoldRuleHeader,
  StyledButton,
  StyledChildRuleContainer,
  StyledDeleteChildRuleContainer,
  StyledDeleteChildRuleIcon,
  StyledRuleBlockContainer,
  StyledRuleHeader,
  StyledRuleHeaderRight,
} from './index.styles';
import { SendToJourney } from './SendToJourney';
import { ExitRuleClickToCopy } from './ExitRuleClickToCopy';

const USER_CONVERTS_DISABLED_TOOL_TIP_CONTENT = `The ${ITERABLE_PURCHASE_EVENT_NAME} conversion event isn't currently supported in exit rules`;

export const RULE_BLOCK_ADD_EVENT = 'ruleblock-add-event';
export const RULE_BLOCK_ADD_PROPERTY = 'ruleblock-add-property';
export const RULE_BLOCK_ADD_JOURNEY_CONVERSION_RULE = 'ruleblock-add-journey-conversion-rule';
export enum RuleBlockConsumer {
  ExitRules = 'ExitRules',
  Other = 'Other',
}

type RuleBlockProps = {
  rule: Rule;
  ruleIdx: number;
  canDelete: boolean;
  canAddRules?: boolean;
  isMaxOneRule?: boolean;
  journeyConversionRuleIdx?: number;
  goToConversionTab?: () => void;
  consumer?: RuleBlockConsumer;
};

export const RuleBlock = ({
  rule,
  ruleIdx,
  canDelete,
  canAddRules = true,
  isMaxOneRule = false,
  journeyConversionRuleIdx,
  goToConversionTab,
  consumer = RuleBlockConsumer.Other,
}: RuleBlockProps) => {
  const { childRules } = rule;
  const {
    setChildRuleEvent,
    setSearchQueries,
    setChildRuleCombinator,
    addChildRule,
    addJourneyConversionRule,
    deleteChildRule,
    deleteRule,
    updateSendToJourney,
    state,
    validationErrors,
    hasCloningErrors,
  } = useRuleBuilder();

  const ruleErrors = validationErrors?.rules?.[ruleIdx];
  const isJourneyExitEventsEnabled = useSelector(getIsJourneyExitEventsEnabled);
  const isJourneyConversionsEnabled = useSelector(selectIsJourneyGoalsFeatureFlagOn);
  const isJourneyWithExitsEnabled = useSelector(selectIsJourneyGoalsWithExitsFeatureFlagOn);
  const journeyConversion = useSelector(getJourneyCurrentConversion);
  const journeyConversionEvent = useSelector(getJourneyCurrentConversionEvent);
  const journeyConversionToolTipEnabled =
    journeyConversionEvent?.eventName === ITERABLE_PURCHASE_EVENT_NAME;
  const isUserConvertsButtonEnabled =
    journeyConversionEvent?.eventName !== ITERABLE_PURCHASE_EVENT_NAME;

  return (
    <StyledRuleBlockContainer
      data-test="ruleblock-container"
      isMaxOneRule={isMaxOneRule}
      hasChildRules={childRules.length > 0}
    >
      {!isMaxOneRule && (
        <StyledRuleHeader>
          <span>
            <StyledBoldRuleHeader>Rule {ruleIdx + 1}:</StyledBoldRuleHeader> Remove users when...
          </span>
          <StyledRuleHeaderRight>
            {isJourneyExitEventsEnabled && rule.id && consumer === RuleBlockConsumer.ExitRules && (
              <ExitRuleClickToCopy id={rule.id} />
            )}
            {canDelete && (
              <IconButton
                size={BUTTON_SIZES.MEDIUM}
                variant={BUTTON_VARIANTS.GHOST}
                onClick={() => deleteRule(ruleIdx)}
                data-test="ruleblock-delete-rule"
              >
                <Delete color={colors.secondaryText} />
              </IconButton>
            )}
          </StyledRuleHeaderRight>
        </StyledRuleHeader>
      )}
      {childRules.map((childRule, childRuleIdx: number) => {
        const childRuleErrors = ruleErrors?.[childRuleIdx];
        const searchQuery = state?.rules[ruleIdx].childRules[childRuleIdx].searchQuery;
        const searchQueries = searchQuery?.searchQueries ?? [];
        const { combinator } = searchQuery;

        const onSearchQueriesUpdateWithActiveFilters = (newQueries: Partial<Query>[]) => {
          // we can't save when comparator type is IS_SET or IS_NOT_SET because the value is null
          // backend expects an empty string as value, so we manually add it
          const saveableNewQueries = newQueries.map((q) =>
            q.comparatorType === IS_SET || q.comparatorType === IS_NOT_SET
              ? { ...q, value: '' }
              : q,
          );
          setSearchQueries(ruleIdx, childRuleIdx, saveableNewQueries);
        };

        const handleEventSelect = (selectedOptions: FilterSegmentSelectInputOption[]) => {
          const { value, ruleType } = selectedOptions?.[0]; // only selecting one
          setChildRuleEvent(ruleIdx, childRuleIdx, value, ruleType);
          // for Subscribe and Unsubscribe, we want to force the user to select a list.
          // if more events need this, lets refactor.
          const isSubOrUnSub = value === 'Subscribed' || value === 'Unsubscribed';
          if (isSubOrUnSub) {
            const dataType = value === 'Subscribed' ? EMAIL_SUBSCRIBE : EMAIL_UNSUBSCRIBE;
            setSearchQueries(
              ruleIdx,
              childRuleIdx,
              hasCloningErrors ? searchQueries : [getQueryStub(dataType)],
            );
          } else {
            setSearchQueries(ruleIdx, childRuleIdx, hasCloningErrors ? searchQueries : []);
          }
        };

        const setCombinator = (newCombinator: string) => {
          setChildRuleCombinator(ruleIdx, childRuleIdx, newCombinator);
        };

        const isUserPropertyType = childRule.ruleType === RULE_TYPES.USER_PROPERTY;

        const handleDeleteChildRule = () => {
          deleteChildRule(ruleIdx, childRuleIdx);
        };

        return (
          <span key={`r${ruleIdx}c${childRuleIdx}`}>
            <StyledChildRuleContainer
              key={childRuleIdx}
              data-test="ruleblock-child-rule"
              isMaxOneRule={isMaxOneRule}
            >
              <StyledDeleteChildRuleContainer
                data-test="ruleblock-childrule-delete"
                onClick={handleDeleteChildRule}
              >
                <StyledDeleteChildRuleIcon color={colors.white} />
              </StyledDeleteChildRuleContainer>
              <ChildRule
                searchQueries={searchQueries}
                onSearchQueriesUpdate={onSearchQueriesUpdateWithActiveFilters}
                handleEventSelect={handleEventSelect}
                childRule={childRule}
                combinator={combinator}
                setCombinator={setCombinator}
                childRuleErrors={childRuleErrors}
                isUserPropertyType={isUserPropertyType}
                isMaxOneRule={isMaxOneRule}
                isDisabled={isJourneyWithExitsEnabled && ruleIdx === journeyConversionRuleIdx}
                isJourneyConversionMetRule={
                  isJourneyWithExitsEnabled && ruleIdx === journeyConversionRuleIdx
                }
              />
            </StyledChildRuleContainer>
            {childRuleIdx < childRules.length - 1 && (
              <StyledBadgeContainer>
                <StyledBadge variant={BADGE_VARIANTS.SCHEDULED}>AND</StyledBadge>
              </StyledBadgeContainer>
            )}
          </span>
        );
      })}
      {canAddRules && (
        <StyledActionArea isMaxOneRule={isMaxOneRule}>
          {isFirstRuleFilledOut([rule]) && (
            <StyledBadge variant={BADGE_VARIANTS.SCHEDULED}>AND</StyledBadge>
          )}
          <StyledButton
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.SECONDARY}
            onClick={() => addChildRule(ruleIdx, RULE_TYPES.CUSTOM_EVENT)}
            data-test={RULE_BLOCK_ADD_EVENT}
          >
            Event occurs
          </StyledButton>
          <StyledButton
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.SECONDARY}
            onClick={() => addChildRule(ruleIdx, RULE_TYPES.USER_PROPERTY)}
            data-test={RULE_BLOCK_ADD_PROPERTY}
          >
            Property changes
          </StyledButton>

          {isJourneyConversionsEnabled &&
            isJourneyWithExitsEnabled &&
            journeyConversionRuleIdx === -1 &&
            (journeyConversionEvent?.eventName ? (
              <Tooltip
                tooltipContent={USER_CONVERTS_DISABLED_TOOL_TIP_CONTENT}
                isEnabled={journeyConversionToolTipEnabled}
              >
                <StyledButton
                  size={BUTTON_SIZES.SMALL}
                  variant={BUTTON_VARIANTS.SECONDARY}
                  onClick={() =>
                    addJourneyConversionRule(
                      ruleIdx,
                      RULE_TYPES.CONVERSION_MET,
                      journeyConversionEvent!.eventName,
                      journeyConversion!.id,
                    )
                  }
                  data-test={RULE_BLOCK_ADD_JOURNEY_CONVERSION_RULE}
                  isDisabled={!isUserConvertsButtonEnabled}
                >
                  User converts
                </StyledButton>
              </Tooltip>
            ) : (
              <Popover defaultOpen={false}>
                <PopoverContent
                  TitleContent={'Setup journey conversion'}
                  LinkContent={<div onClick={goToConversionTab}>Take me there</div>}
                >
                  Select a converesion event for this journey in order to exit users.
                </PopoverContent>
                <PopoverTrigger>
                  <StyledButton
                    size={BUTTON_SIZES.SMALL}
                    variant={BUTTON_VARIANTS.SECONDARY}
                    data-test="ruleblock-add-journey-conversion-rule"
                  >
                    User converts
                  </StyledButton>
                </PopoverTrigger>
              </Popover>
            ))}
        </StyledActionArea>
      )}
      {isJourneyWithExitsEnabled && consumer === RuleBlockConsumer.ExitRules && (
        <SendToJourney
          rule={rule}
          onSelect={(sendToJourneyMetadata: SendToJourneyMetadata | null) =>
            updateSendToJourney(ruleIdx, sendToJourneyMetadata)
          }
        />
      )}
    </StyledRuleBlockContainer>
  );
};
